import fetchWithAuth from "@/api/secureFetch";
import { getBatchJobStatus, type JobStatus } from "@/api/workProcessor";
import type { ProcessingJob, ProcessingJobActivity, ProcessingJobKind, ProcessingJobsResponse } from "./types";
import { isActiveProcessingJob, normalizeProcessingJobStatus } from "./types";

type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord | null =>
    value !== null && typeof value === "object" && !Array.isArray(value) ? (value as UnknownRecord) : null;

const firstString = (...values: unknown[]): string | null => {
    for (const value of values) {
        if (typeof value === "string" && value.trim()) return value.trim();
        if (typeof value === "number" && Number.isFinite(value)) return String(value);
    }
    return null;
};

const asProgress = (value: unknown): number | null => {
    const numeric = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
    return Number.isFinite(numeric) ? Math.max(0, Math.min(100, Math.round(numeric))) : null;
};

/**
 * The processing-jobs API serializes timezone-naive database values as UTC.
 * Make that assumption explicit for the browser while leaving timestamps that
 * already carry an offset untouched.
 */
export const normalizeApiTimestamp = (value: unknown): string | null => {
    const timestamp = firstString(value);
    if (!timestamp) return null;
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(timestamp)) return timestamp;
    return /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(timestamp) ? timestamp : `${timestamp}Z`;
};

const processingJobKind = (raw: UnknownRecord): ProcessingJobKind => {
    const value = firstString(raw.kind, raw.entity_type, raw.resource_type, raw.target_type, raw.job_type)?.toLowerCase();
    if (value === "product" || value === "product_build") return "product";
    if (value?.includes("work_product") || value === "workproduct") return "work_product";
    return raw.work_product_id != null || raw.workProductId != null ? "work_product" : "product";
};

const canonicalLiveStatus = (status: unknown): string => {
    const normalized = normalizeProcessingJobStatus(status);
    if (["complete", "completed", "success", "succeeded"].includes(normalized)) return "completed";
    if (["canceled", "cancelled", "error", "failed"].includes(normalized)) return "failed";
    if (["recovering", "started", "running", "processing", "section_completed", "in_progress"].includes(normalized)) return "in_progress";
    return "queued";
};

export const applyLiveProcessingJobStatus = (job: ProcessingJob, live: JobStatus): ProcessingJob => {
    const liveStatus = canonicalLiveStatus(live.status);
    const liveProgress = liveStatus === "completed" ? 100 : asProgress(live.progress);
    const message = firstString(live.description, live.message, live.error) ?? job.message;
    const activities = job.activities.map(activity => {
        if (job.kind !== "product" || activity.type !== "base_generation") return activity;
        return {
            ...activity,
            status: liveStatus,
            progress: liveProgress,
            message
        };
    });
    const pendingPostProcessorActivity = job.kind === "product"
        ? activities.find(activity =>
              activity.type !== "base_generation"
              && !["complete", "completed", "success", "succeeded"].includes(normalizeProcessingJobStatus(activity.status))
          )
        : null;

    if (pendingPostProcessorActivity) {
        return { ...job, activities };
    }

    return {
        ...job,
        status: liveStatus,
        progress: liveProgress,
        message,
        activities
    };
};

async function enrichWithLiveProgress(jobs: ProcessingJob[], token: string): Promise<ProcessingJob[]> {
    const activeJobs = jobs.filter(isActiveProcessingJob);
    if (activeJobs.length === 0) return jobs;
    try {
        const statuses = await getBatchJobStatus(activeJobs.map(job => job.id), token);
        return jobs.map(job => {
            const live = statuses[job.id];
            return live ? applyLiveProcessingJobStatus(job, live) : job;
        });
    } catch {
        // The database list remains authoritative and available when the
        // processor is restarting. The next five-second poll retries.
        return jobs;
    }
}

const normalizeProcessingJobActivity = (value: unknown, index: number): ProcessingJobActivity | null => {
    const raw = asRecord(value);
    if (!raw) return null;
    const type = firstString(raw.type, raw.activity_type, raw.activityType) ?? "activity";
    return {
        id: firstString(raw.id, raw.activity_id, raw.activityId) ?? `activity-${index}`,
        type,
        label: firstString(raw.label, raw.name, raw.title) ?? type.replace(/[_-]+/g, " "),
        status: normalizeProcessingJobStatus(raw.status ?? raw.state),
        progress: asProgress(raw.progress ?? raw.progress_percent ?? raw.percentage),
        message: firstString(raw.message, raw.description, raw.error),
        externalJobId: firstString(raw.external_job_id, raw.externalJobId),
        createdAt: normalizeApiTimestamp(firstString(raw.created_at, raw.createdAt)),
        updatedAt: normalizeApiTimestamp(firstString(raw.updated_at, raw.updatedAt))
    };
};

export function normalizeProcessingJob(value: unknown, index = 0): ProcessingJob | null {
    const raw = asRecord(value);
    if (!raw) return null;

    const product = asRecord(raw.product);
    const workProduct = asRecord(raw.work_product) ?? asRecord(raw.workProduct);
    const build = asRecord(raw.build) ?? asRecord(raw.product_build) ?? asRecord(raw.productBuild);
    const rawActivities = Array.isArray(raw.activities) ? raw.activities : Array.isArray(build?.activities) ? build.activities : [];
    const currentActivity = asRecord(raw.current_activity) ?? asRecord(raw.currentActivity);
    const id = firstString(raw.id, raw.job_id, raw.jobId, raw.external_job_id) ?? `processing-job-${index}`;

    return {
        id,
        kind: processingJobKind(raw),
        status: normalizeProcessingJobStatus(raw.status ?? raw.state),
        progress: asProgress(raw.progress ?? raw.progress_percent ?? raw.percentage),
        message: firstString(raw.message, raw.description, raw.error),
        productId: firstString(raw.product_id, raw.project_id, raw.productId, product?.id),
        productName: firstString(raw.product_name, raw.project_name, raw.productName, product?.name, product?.title),
        workProductId: firstString(raw.work_product_id, raw.workProductId, workProduct?.id),
        workProductName: firstString(
            raw.work_product_name,
            raw.work_product_title,
            raw.workProductName,
            workProduct?.name,
            workProduct?.title,
            processingJobKind(raw) === "work_product" ? (raw.label ?? raw.title) : null
        ),
        currentActivityId: firstString(raw.current_activity_id, raw.currentActivityId, currentActivity?.id),
        activities: rawActivities
            .map((activity, activityIndex) => normalizeProcessingJobActivity(activity, activityIndex))
            .filter((activity): activity is ProcessingJobActivity => activity !== null),
        createdAt: normalizeApiTimestamp(firstString(raw.queued_at, raw.created_at, raw.queuedAt, raw.createdAt)),
        startedAt: normalizeApiTimestamp(firstString(raw.started_at, raw.startedAt)),
        updatedAt: normalizeApiTimestamp(firstString(raw.updated_at, raw.updatedAt)),
        completedAt: normalizeApiTimestamp(firstString(raw.completed_at, raw.finished_at, raw.completedAt, raw.finishedAt))
    };
}

async function responseError(response: Response, fallback: string): Promise<Error> {
    const body = await response.json().catch(() => null);
    const record = asRecord(body);
    return new Error(firstString(record?.error, record?.message) ?? fallback);
}

function jobsFromPayload(payload: unknown): ProcessingJob[] {
    const record = asRecord(payload);
    const rawJobs = Array.isArray(payload) ? payload : Array.isArray(record?.jobs) ? record.jobs : Array.isArray(record?.items) ? record.items : [];
    return rawJobs.map((job, index) => normalizeProcessingJob(job, index)).filter((job): job is ProcessingJob => job !== null);
}

export async function fetchProcessingJobs(token: string, signal?: AbortSignal): Promise<ProcessingJobsResponse> {
    const response = await fetchWithAuth("/api/processing-jobs", token, { signal });
    if (!response.ok) {
        throw await responseError(response, `Failed to load background activity (${response.status})`);
    }
    const jobs = jobsFromPayload(await response.json());
    return { jobs: await enrichWithLiveProgress(jobs, token) };
}

export async function clearFinishedProcessingJobs(token: string): Promise<ProcessingJobsResponse | null> {
    const response = await fetchWithAuth("/api/processing-jobs/clear-finished", token, { method: "POST" });
    if (!response.ok) {
        throw await responseError(response, `Failed to clear finished activity (${response.status})`);
    }
    if (response.status === 204) return null;
    const payload = await response.json().catch(() => null);
    if (payload === null) return null;
    const record = asRecord(payload);
    const hasJobs = Array.isArray(payload) || Array.isArray(record?.jobs) || Array.isArray(record?.items);
    return hasJobs ? { jobs: jobsFromPayload(payload) } : null;
}

export async function retryProductBuildActivity(projectId: string, activityType: string, token: string): Promise<void> {
    const response = await fetchWithAuth(`/api/project/${encodeURIComponent(projectId)}/product-build/retry`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ activity_type: activityType })
    });
    if (!response.ok) {
        throw await responseError(response, `Failed to retry Product build (${response.status})`);
    }
}
